import { useId } from "react";
import { Button } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";

/** `HH:MM`, 24-hour, Western digits in both locales (round 10). */
export function formatBoardClock(at: number): string {
  const date = new Date(at);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * THE DISCONNECTED BAND (T12, founder F4) — the board saying, loudly and in
 * words, that it may be wrong.
 *
 * Shown by the board while its live updates are down (the same inputs as
 * `RealtimeNotice`, which yields on `/kitchen` so there is one warning, not
 * two): a full-width red band «الشاشة غير متصلة — آخر تحديث HH:MM», the line
 * «قد تكون هناك طلبات لا تظهر.», and one big «تحديث الشاشة».
 *
 * It schedules NOTHING. No countdown, no automatic retry beyond the three the
 * credential exchange already makes, and never «جارٍ إعادة الاتصال» (the shell's
 * `systemScreens.offlineBadge`): once retries stop, a board that kept saying it
 * was reconnecting would be making a promise nobody keeps. Recovery is the
 * person's tap, which the board turns into one listener re-attach and one read.
 *
 * A tap while the DEVICE is offline cannot read anything, so instead of a
 * button that silently stays disabled the band says why under it
 * («الجهاز غير متصل بالشبكة — …»); the page clears it when the browser's
 * `online` event fires. Still no timer: the browser's own event is the signal.
 */
export function KitchenOfflineBand({
  lastUpdatedAt,
  onRefresh,
  refreshing = false,
  waitingForNetwork = false,
}: {
  /** `board.dataUpdatedAt` — 0 when the board never arrived. */
  lastUpdatedAt: number;
  onRefresh: () => void;
  refreshing?: boolean;
  /** A tap found the device offline; the page clears it on `online`. */
  waitingForNetwork?: boolean;
}) {
  const t = useTranslate();
  const networkNoteId = useId();
  return (
    <div
      role="alert"
      data-ui="kitchen-offline"
      data-testid="kitchen-offline"
      className="no-print flex flex-wrap items-center gap-x-6 gap-y-3"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p data-ui="kitchen-offline-title" data-testid="kitchen-offline-title">
          {lastUpdatedAt > 0
            ? t((m) => m.kitchen.offlineTitle, { time: formatBoardClock(lastUpdatedAt) })
            : t((m) => m.kitchen.offlineTitleNever)}
        </p>
        <p data-ui="kitchen-offline-body">{t((m) => m.kitchen.offlineBody)}</p>
      </div>
      <div data-ui="kitchen-offline-action" className="flex flex-col items-start gap-2">
        <Button
          variant="secondary"
          size="lg"
          data-testid="kitchen-offline-refresh"
          disabled={refreshing || waitingForNetwork}
          aria-describedby={waitingForNetwork ? networkNoteId : undefined}
          onClick={onRefresh}
        >
          {t((m) => m.kitchen.offlineRefresh)}
        </Button>
        {waitingForNetwork ? (
          <p
            id={networkNoteId}
            data-ui="kitchen-offline-network"
            data-testid="kitchen-offline-network"
          >
            {t((m) => m.kitchen.offlineNoNetwork)}
          </p>
        ) : null}
      </div>
    </div>
  );
}
